import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../lib/api';
import type { ClientProfile, Decision, Me, Solicitation, SolicitationDetail, SolicitationList, SolicitationSort, SolicitationStatus, Tag } from '../lib/api';
import { daysUntil, fmtDate, fmtDateTime, titleCase } from '../lib/format';
import { Empty, Kpi, Modal, PageHead, Pill } from '../components/ui';
import { TagChips } from '../components/tags';
import { DecisionButtons, DecisionPill, RationaleLine, ScoreBadge } from '../components/matches';

const CHANNELS = [
  { id: 'all', label: 'All channels' }, { id: 'sam', label: 'SAM.gov' }, { id: 'baa', label: 'BAA / CSO' },
  { id: 'sbir', label: 'SBIR / STTR' }, { id: 'consortium', label: 'Consortium' }, { id: 'ota', label: 'OTA' },
];
const STATUSES: { id: SolicitationStatus; label: string }[] = [
  { id: 'open', label: 'Open' }, { id: 'closed', label: 'Closed' }, { id: 'all', label: 'Open + closed' },
];
const DUE = [{ id: '', label: 'Any due date' }, { id: '14', label: 'Due ≤ 14 days' }, { id: '30', label: 'Due ≤ 30 days' }];
const SORTS: { id: SolicitationSort; label: string; needsClient?: boolean }[] = [
  { id: 'fit', label: 'Best fit', needsClient: true },
  { id: 'due_asc', label: 'Expiring soonest' }, { id: 'due_desc', label: 'Expiring latest' },
  { id: 'posted', label: 'Newest posted' }, { id: 'title', label: 'Title A to Z' },
];
const PAGE = 100;

export function Solicitations({ params, me }: { params: string[]; me: Me }) {
  const routeQ = params[0] === 'q' ? (params[1] ?? '') : '';
  const routeId = params[0] === 'id' ? (params[1] ?? null) : null;

  const [tags, setTags] = useState<Tag[]>([]);
  const [clients, setClients] = useState<ClientProfile[]>([]);
  const [q, setQ] = useState(routeQ);
  const [draft, setDraft] = useState(routeQ);
  const [channel, setChannel] = useState('all');
  const [status, setStatus] = useState<SolicitationStatus>('open');
  const [due, setDue] = useState('');
  const [tag, setTag] = useState('');
  const [client, setClient] = useState('');
  const [sort, setSort] = useState<SolicitationSort>('due_asc');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<SolicitationList | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(routeId);
  const requestSeq = useRef(0);

  useEffect(() => {
    api<Tag[]>('/api/tags').then(setTags).catch(() => setTags([]));
    api<{ items: ClientProfile[] }>('/api/clients').then((r) => setClients(r.items)).catch(() => setClients([]));
  }, []);
  useEffect(() => { setQ(routeQ); setDraft(routeQ); setOffset(0); }, [routeQ]);
  useEffect(() => { setSelected(routeId); }, [routeId]);

  // Any filter or sort change restarts from the first page; "Load more" only advances offset.
  const change = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setOffset(0); };
  const chooseClient = (id: string) => {
    setClient(id);
    if (id) setSort('fit'); else if (sort === 'fit') setSort('due_asc');
    setOffset(0);
  };

  useEffect(() => {
    const p = new URLSearchParams({ limit: String(PAGE), offset: String(offset), status, sort });
    if (q) p.set('q', q);
    if (channel !== 'all') p.set('channel', channel);
    if (due) p.set('due_within_days', due);
    if (tag) p.set('tag', tag);
    if (client) p.set('client', client);
    const seq = ++requestSeq.current;
    setLoading(true);
    api<SolicitationList>(`/api/solicitations?${p.toString()}`)
      .then((r) => {
        if (seq !== requestSeq.current) return;
        setData((prev) => (offset > 0 && prev ? { ...r, items: [...prev.items, ...r.items] } : r));
      })
      .catch(() => { if (seq === requestSeq.current) setData(null); })
      .finally(() => { if (seq === requestSeq.current) setLoading(false); });
  }, [q, channel, status, due, tag, client, sort, offset]);

  const items = data?.items ?? [];
  const summary = data?.summary;
  const closeDetail = useCallback(() => { setSelected(null); if (routeId) location.hash = '/solicitations'; }, [routeId]);
  const filtered = !!q || channel !== 'all' || !!due || !!tag;
  const clientName = clients.find((c) => c.id === client)?.name;
  const onDecision = (solId: string, d: Decision) =>
    setData((prev) => (prev ? { ...prev, items: prev.items.map((i) => (i.id === solId ? { ...i, fit_decision: d } : i)) } : prev));

  return (
    <>
      <PageHead
        title="Solicitations"
        sub="Released opportunities across SAM.gov, BAAs, SBIR/STTR and consortium channels, tagged by technology, scored per client, every amendment kept"
        actions={
          <>
            <form className="search" onSubmit={(e: FormEvent) => { e.preventDefault(); change(setQ)(draft.trim()); }} style={{ minWidth: 260 }}>
              <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Filter by title or number…" />
            </form>
            <label className="sort-control">
              <span className="muted sm">Fit for</span>
              <select className="select" value={client} onChange={(e) => chooseClient(e.target.value)}>
                <option value="">No client</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="sort-control">
              <span className="muted sm">Sort</span>
              <select className="select" value={sort} onChange={(e) => change(setSort)(e.target.value as SolicitationSort)}>
                {SORTS.filter((s) => !s.needsClient || client).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </label>
          </>
        }
      />
      <div className="kpi-row">
        <Kpi label="Open" value={summary ? summary.open : '…'} hint={filtered ? 'in this filter' : undefined} />
        <Kpi label="Closed" value={summary ? summary.closed : '…'} hint="past due, archived or withdrawn" />
        <Kpi label="Closing ≤ 14 days" value={summary ? summary.due_14d : '…'} />
        <Kpi label="Amended since first seen" value={summary ? summary.amended : '…'} />
        <Kpi label="With analyst notes" value={summary ? summary.noted : '…'} />
      </div>
      <div className="filter-bar">
        {STATUSES.map((s) => <button key={s.id} className={`filter-btn ${status === s.id ? 'active' : ''}`} onClick={() => change(setStatus)(s.id)}>{s.label}</button>)}
        <span className="filter-gap" />
        {CHANNELS.map((c) => <button key={c.id} className={`filter-btn ${channel === c.id ? 'active' : ''}`} onClick={() => change(setChannel)(c.id)}>{c.label}</button>)}
        <span className="filter-gap" />
        {DUE.map((d) => <button key={d.id} className={`filter-btn ${due === d.id ? 'active' : ''}`} onClick={() => change(setDue)(d.id)}>{d.label}</button>)}
      </div>
      <div className="filter-bar">
        <button className={`filter-btn ${tag === '' ? 'active' : ''}`} onClick={() => change(setTag)('')}>Any technology</button>
        {tags.map((t) => (
          <button key={t.id} className={`filter-btn ${tag === t.id ? 'active' : ''}`} onClick={() => change(setTag)(t.id)}>
            {t.label} <span className="count">{t.open_notices}</span>
          </button>
        ))}
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>{data ? `${items.length.toLocaleString()} of ${data.total.toLocaleString()} ${status === 'all' ? '' : status + ' '}notices${clientName ? ` · fit for ${clientName}` : ''}` : 'Loading…'}</h2>
          <span className="muted sm">{SORTS.find((s) => s.id === sort)?.label}</span>
        </div>
        <div className="panel-body table-wrap">
          {loading && !data ? <Empty>Loading…</Empty> : items.length === 0 ? (
            <Empty>
              {filtered || status !== 'open'
                ? 'Nothing matches these filters.'
                : <>No open solicitations yet. The scheduled SAM.gov pull runs twice a day; run <code>npm run ingest:sam -- --days 2</code> on the server to load now.</>}
            </Empty>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  {client && <th>Fit</th>}
                  <th>Solicitation</th><th>Type</th><th>Issuing organization</th><th>Set-aside</th><th>Posted</th>
                  <th>Expires</th>{status !== 'open' && <th>Status</th>}<th className="num">Versions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <Row key={s.id} s={s} tags={tags} showStatus={status !== 'open'} showFit={!!client} canDecide={me.role !== 'viewer'}
                    onOpen={() => setSelected(s.id)} onDecision={(d) => onDecision(s.id, d)} />
                ))}
              </tbody>
            </table>
          )}
        </div>
        {data && items.length < data.total && (
          <div className="panel-body" style={{ borderTop: '1px solid var(--line-2)', textAlign: 'center' }}>
            <button className="ghost-btn" disabled={loading} onClick={() => setOffset(items.length)}>
              {loading ? 'Loading…' : `Load ${Math.min(PAGE, data.total - items.length)} more`}
            </button>
          </div>
        )}
      </div>

      {selected && <SolicitationModal id={selected} me={me} tags={tags} onClose={closeDetail} />}
    </>
  );
}

function ExpiryCell({ s }: { s: Solicitation }) {
  if (!s.response_due_at) {
    return <><span className="muted">No deadline</span>{s.archive_at && <div className="muted sm">archives {fmtDate(s.archive_at)}</div>}</>;
  }
  const d = daysUntil(s.response_due_at);
  if (d === null) return <span className="muted">—</span>;
  if (d < 0) return <>{fmtDate(s.response_due_at)}<div className="muted sm">closed {-d} day{d === -1 ? '' : 's'} ago</div></>;
  return <>{fmtDate(s.response_due_at)}<div className={`sm ${d <= 3 ? 'neg' : 'muted'}`}>{d === 0 ? 'today' : `${d} day${d === 1 ? '' : 's'}`}</div></>;
}

function Row({ s, tags, showStatus, showFit, canDecide, onOpen, onDecision }: {
  s: Solicitation; tags: Tag[]; showStatus: boolean; showFit: boolean; canDecide: boolean; onOpen: () => void; onDecision: (d: Decision) => void;
}) {
  return (
    <tr className="clickable" onClick={onOpen}>
      {showFit && (
        <td onClick={(e) => e.stopPropagation()}>
          {s.match_id ? (
            <>
              <ScoreBadge score={s.fit} />
              {canDecide
                ? <div style={{ marginTop: 6 }}><DecisionButtons matchId={s.match_id} decision={s.fit_decision ?? 'pending'} onChanged={onDecision} /></div>
                : s.fit_decision && s.fit_decision !== 'pending' && <div style={{ marginTop: 6 }}><DecisionPill decision={s.fit_decision} /></div>}
            </>
          ) : <span className="muted sm">no fit</span>}
        </td>
      )}
      <td>
        <strong>{s.title}</strong>
        <div className="muted sm">{s.solicitation_number ?? '—'}{s.notes > 0 && <span className="tag new">{s.notes} NOTE{s.notes > 1 ? 'S' : ''}</span>}</div>
        {s.tags.length > 0 && <div style={{ marginTop: 4 }}><TagChips tags={s.tags} all={tags} max={3} /></div>}
      </td>
      <td>{titleCase(s.notice_type)}</td>
      <td className="muted">{s.issuing_org_path?.split('.').slice(-1)[0] ?? '—'}</td>
      <td className="muted">{s.set_aside ?? '—'}</td>
      <td>{fmtDate(s.posted_at)}</td>
      <td><ExpiryCell s={s} /></td>
      {showStatus && <td><Pill kind={s.is_open ? 'ok' : 'muted'}>{s.is_open ? 'Open' : 'Closed'}</Pill></td>}
      <td className="num">{s.versions}</td>
    </tr>
  );
}

function SolicitationModal({ id, me, tags, onClose }: { id: string; me: Me; tags: Tag[]; onClose: () => void }) {
  const [d, setD] = useState<SolicitationDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [kind, setKind] = useState('note');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api<SolicitationDetail>(`/api/solicitations/${id}`).then(setD).catch((e: Error) => setErr(e.message));
  }, [id]);
  useEffect(load, [load]);

  async function addNote(e: FormEvent) {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api(`/api/solicitations/${id}/notes`, { method: 'POST', json: { body: note.trim(), kind } });
      setNote('');
      load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const dueDays = d ? daysUntil(d.response_due_at) : null;
  return (
    <Modal title={d?.title ?? 'Loading…'} onClose={onClose}>
      {err && <div className="error-text">{err}</div>}
      {d && (
        <>
          <div className="filter-bar" style={{ marginBottom: 12 }}>
            <Pill kind={d.is_open ? 'ok' : 'muted'}>{d.is_open ? 'Open' : 'Closed'}</Pill>
            <Pill kind="info">{titleCase(d.notice_type)}</Pill>
            <Pill kind="muted">{d.channel.toUpperCase()}</Pill>
            <TagChips tags={d.tags} all={tags} max={6} showConfidence />
            {d.url && <a className="link" href={d.url} target="_blank" rel="noreferrer">Open at source ↗</a>}
          </div>
          <dl className="kv">
            <dt>Number</dt><dd>{d.solicitation_number ?? '—'}</dd>
            <dt>Issuing org</dt><dd>{d.issuing_org_path ?? '—'}</dd>
            <dt>NAICS / PSC</dt><dd>{d.naics ?? '—'} / {d.psc ?? '—'}</dd>
            <dt>Set-aside</dt><dd>{d.set_aside ?? 'None'}</dd>
            <dt>Posted</dt><dd>{fmtDate(d.posted_at)}</dd>
            <dt>Response due</dt><dd>{d.response_due_at ? `${fmtDateTime(d.response_due_at)}${dueDays !== null ? (dueDays < 0 ? ` (closed ${-dueDays} days ago)` : ` (${dueDays} days left)`) : ''}` : 'No deadline'}</dd>
            <dt>Archive</dt><dd>{fmtDate(d.archive_at)}</dd>
            <dt>Tracked since</dt><dd>{fmtDateTime(d.first_seen_at)} · last confirmed {fmtDateTime(d.last_seen_at)}</dd>
          </dl>

          <h3 style={{ fontSize: 13, marginTop: 18, marginBottom: 6 }}>Fit by client ({d.matches.length})</h3>
          {d.matches.length === 0 && <div className="muted sm">No client profile scores against this notice (no shared technology tag or NAICS).</div>}
          {d.matches.map((m) => (
            <div className="version" key={m.id} style={{ alignItems: 'flex-start' }}>
              <time><ScoreBadge score={m.score} /></time>
              <span style={{ flex: 1 }}>
                <a className="link" href={`#/clients/${m.client_id}`}>{m.client_name}</a>
                <RationaleLine r={m.rationale} tags={tags} />
              </span>
              {me.role !== 'viewer'
                ? <DecisionButtons matchId={m.id} decision={m.decision} onChanged={load} />
                : m.decision !== 'pending' && <DecisionPill decision={m.decision} />}
            </div>
          ))}

          <h3 style={{ fontSize: 13, marginTop: 18, marginBottom: 6 }}>Version history ({d.versions.length})</h3>
          {d.versions.map((v, i) => (
            <div className="version" key={v.id}>
              <time>{fmtDateTime(v.observed_at)}</time>
              <span>{i === d.versions.length - 1 ? 'First observed' : v.changed_fields.length ? `Changed: ${v.changed_fields.map(titleCase).join(', ')}` : 'Re-observed'}</span>
            </div>
          ))}

          <h3 style={{ fontSize: 13, marginTop: 18, marginBottom: 6 }}>Analyst notes ({d.notes.length})</h3>
          {d.notes.length === 0 && <div className="muted sm">No notes yet.</div>}
          {d.notes.map((n) => (
            <div className="note" key={n.id}>
              <div><span className="tag new" style={{ marginLeft: 0, marginRight: 8 }}>{titleCase(n.kind).toUpperCase()}</span>{n.body}</div>
              <div className="meta">{n.author ?? 'Unknown'} · {fmtDateTime(n.created_at)} · {n.visibility}</div>
            </div>
          ))}
          {me.role !== 'viewer' && (
            <form onSubmit={addNote} style={{ marginTop: 12 }}>
              <div className="form-grid">
                <div className="field span-2">
                  <label htmlFor="note-body">Add a note</label>
                  <textarea id="note-body" className="textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Bid/no-bid rationale, teaming targets, strategic fit, follow-ups…" />
                </div>
                <div className="field">
                  <label htmlFor="note-kind">Kind</label>
                  <select id="note-kind" className="select" value={kind} onChange={(e) => setKind(e.target.value)}>
                    {['note', 'bid_decision', 'teaming', 'strategic_fit', 'competitiveness', 'follow_up', 'recommendation'].map((k) => <option key={k} value={k}>{titleCase(k)}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-actions"><button className="primary-btn" type="submit" disabled={saving || !note.trim()}>{saving ? 'Saving…' : 'Save note'}</button></div>
            </form>
          )}
        </>
      )}
    </Modal>
  );
}
